// Rule-group data model + the low-level `<condition-builder>` Knockout component
// used in the advanced mode of `<condition-builder-filter>`. This module knows
// nothing about pills, popovers, or filter UI — only the tree shape
// (Rule / RuleGroup), its (de)serialization, and the recursive editor component.
//
// Screens should reach for `RuleGroupController`, which bundles tree lifecycle
// + snapshot + hydration behind a single object.

import * as ko from 'knockout';
import i18n from '../i18n';

import { FormSelectSearchConfiguration } from './form_select_search';
import { IdData } from './list_filters';
import type { I18nText } from '../i18n_text';

const template = require('raw-loader!../../templates/components/condition_builder.html').default;

export enum Combinator {
  And = 'and',
  Or = 'or',
}

export interface Rule<T extends IdData> {
  type: 'rule';
  entity: T;
}

export interface RuleGroup<T extends IdData> {
  type: 'group';
  combinator: ko.Observable<Combinator>;
  rules: ko.ObservableArray<Rule<T> | RuleGroup<T>>;
}

export interface ConditionBuilderParams<T extends IdData> {
  group: RuleGroup<T>;
  searchConfig: FormSelectSearchConfiguration<T>;
  getDisplayName?: (item: T) => string | I18nText;
  onChange: () => void;
  onRemove?: () => void;
  maxDepth?: number;
  depth?: number;
  parentCombinator?: ko.Observable<Combinator>;
}

export function createRuleGroup<T extends IdData>(
  combinator: Combinator = Combinator.Or
): RuleGroup<T> {
  return {
    type: 'group',
    combinator: ko.observable(combinator),
    rules: ko.observableArray<Rule<T> | RuleGroup<T>>([]),
  };
}

export interface SerializedRule {
  value: string;
}

export interface SerializedRuleGroup {
  combinator: Combinator;
  rules: (SerializedRule | SerializedRuleGroup)[];
}

export function serializeRuleGroup<T extends IdData>(
  node: Rule<T> | RuleGroup<T>
): SerializedRule | SerializedRuleGroup {
  if (node.type === 'rule') {
    return { value: ko.unwrap(node.entity.id!) };
  }
  return {
    combinator: node.combinator(),
    rules: node.rules().map(serializeRuleGroup),
  };
}

export function serializeRootGroup<T extends IdData>(
  rootGroup: RuleGroup<T>
): SerializedRuleGroup | null {
  if (rootGroup.rules().length === 0) {
    return null;
  }
  return serializeRuleGroup(rootGroup) as SerializedRuleGroup;
}

export function collectRuleIds(
  node: SerializedRule | SerializedRuleGroup
): string[] {
  if ('value' in node) {
    return [node.value];
  }
  return node.rules.flatMap(collectRuleIds);
}

export function hydrateRuleGroup<T extends IdData>(
  serialized: SerializedRuleGroup,
  entitiesById: Map<string, T>
): RuleGroup<T> {
  const group = createRuleGroup<T>(serialized.combinator);
  for (const child of serialized.rules) {
    if ('value' in child) {
      const entity = entitiesById.get(child.value);
      if (entity) {
        group.rules.push({ type: 'rule', entity: entity });
      }
    } else {
      group.rules.push(hydrateRuleGroup(child, entitiesById));
    }
  }
  return group;
}

export function countRules<T extends IdData>(
  node: Rule<T> | RuleGroup<T>
): number {
  if (node.type === 'rule') {
    return 1;
  }
  return node.rules().reduce((sum, child) => sum + countRules(child), 0);
}

export function fitsSimpleMode<T extends IdData>(
  group: RuleGroup<T>
): boolean {
  return group.rules().every((rule) => rule.type === 'rule');
}

// JS counterpart of `build_rule_group_q` in `backend/main/models/rule_node.py`;
// keep the boolean semantics in sync. An empty group (or null) places no
// constraint and matches everything.
export function evaluateRuleGroup<T extends IdData>(
  group: RuleGroup<T> | null,
  ids: Set<NonNullable<T['id']>>
): boolean {
  if (!group || group.rules().length === 0) {
    return true;
  }
  const isAnd = group.combinator() === Combinator.And;
  for (const child of group.rules()) {
    const childMatches =
      child.type === 'rule'
        ? ids.has(ko.unwrap(child.entity.id!))
        : evaluateRuleGroup(child, ids);
    if (isAnd && !childMatches) {
      return false;
    }
    if (!isAnd && childMatches) {
      return true;
    }
  }
  return isAnd;
}

// Screen-facing facade: owns a root group, exposes a reactive snapshot for URL
// sync / list reloads, and centralizes reset + hydrate. Lets callers import a
// single symbol instead of half a dozen helpers.
export class RuleGroupController<T extends IdData> {
  readonly group: RuleGroup<T>;
  readonly snapshot: ko.PureComputed<SerializedRuleGroup | null>;

  constructor(combinator: Combinator = Combinator.Or) {
    this.group = createRuleGroup<T>(combinator);
    this.snapshot = ko.pureComputed(() => serializeRootGroup(this.group));
  }

  reset(): void {
    if (this.group.rules().length > 0) {
      this.group.rules([]);
    }
  }

  async hydrate(
    serialized: SerializedRuleGroup,
    lookup: (ids: string[]) => Promise<Map<string, T>>
  ): Promise<void> {
    const ids = collectRuleIds(serialized);
    const entitiesById = await lookup(ids);
    const hydrated = hydrateRuleGroup(serialized, entitiesById);
    this.group.combinator(hydrated.combinator());
    this.group.rules(hydrated.rules());
  }
}

class ConditionBuilder<T extends IdData> {
  group: RuleGroup<T>;
  searchConfig: FormSelectSearchConfiguration<T>;
  getDisplayName: (item: T) => string | I18nText;
  onChange: () => void;
  onRemove: (() => void) | null;
  maxDepth: number;
  depth: number;
  parentCombinator?: ko.Observable<Combinator>;

  addSearchConfig: FormSelectSearchConfiguration<T>;
  combinatorLabel: ko.Computed<string>;
  canAddGroup: boolean;
  canAddSubFilter: boolean;
  subFilterLabel: ko.Computed<string>;
  subSectionHeader: ko.Computed<string>;
  conditionCount: ko.Computed<number>;

  private presentEntities: ko.ObservableArray<T> = ko.observableArray<T>([]);
  private subscriptions: ko.Subscription[] = [];

  private allMatchLabel = i18n.t('all');
  private anyMatchLabel = i18n.t('any');
  private andAnyButtonLabel = i18n.t('And any of these…');
  private orAllButtonLabel = i18n.t('Or all of these…');
  private orAllOfHeader = i18n.t('OR all of:');
  private orAnyOfHeader = i18n.t('OR any of:');
  private andAllOfHeader = i18n.t('AND all of:');
  private andAnyOfHeader = i18n.t('AND any of:');

  constructor(params: ConditionBuilderParams<T>) {
    this.group = params.group;
    this.searchConfig = params.searchConfig;
    this.getDisplayName = params.getDisplayName ?? params.searchConfig.getSummaryName;
    this.onChange = params.onChange;
    this.onRemove = params.onRemove ?? null;
    this.maxDepth = params.maxDepth ?? 1;
    this.depth = params.depth ?? 0;
    this.parentCombinator = params.parentCombinator;

    this.canAddGroup = this.depth < this.maxDepth;
    this.canAddSubFilter = this.depth < this.maxDepth;

    this.combinatorLabel = ko.pureComputed(() => {
      return this.group.combinator() === Combinator.And
        ? this.allMatchLabel()
        : this.anyMatchLabel();
    });

    this.subFilterLabel = ko.pureComputed(() => {
      return this.group.combinator() === Combinator.And
        ? this.andAnyButtonLabel()
        : this.orAllButtonLabel();
    });

    this.subSectionHeader = ko.pureComputed(() => {
      const parentIsAnd = this.parentCombinator?.() === Combinator.And;
      const ownIsAnd = this.group.combinator() === Combinator.And;
      if (parentIsAnd) {
        return ownIsAnd ? this.andAllOfHeader() : this.andAnyOfHeader();
      }
      return ownIsAnd ? this.orAllOfHeader() : this.orAnyOfHeader();
    });

    this.conditionCount = ko.pureComputed(() => countRules(this.group));

    const syncPresent = () => {
      this.presentEntities(
        this.group
          .rules()
          .filter((rule): rule is Rule<T> => rule.type === 'rule')
          .map((rule) => rule.entity)
      );
    };
    syncPresent();
    this.subscriptions.push(this.group.rules.subscribe(syncPresent));

    this.subscriptions.push(
      this.group.combinator.subscribe((newCombinator) => {
        const inverse =
          newCombinator === Combinator.And ? Combinator.Or : Combinator.And;
        let flipped = false;
        for (const rule of this.group.rules()) {
          if (rule.type === 'group' && rule.combinator() !== inverse) {
            rule.combinator(inverse);
            flipped = true;
          }
        }
        if (flipped) {
          this.onChange();
        }
      })
    );

    this.addSearchConfig = {
      ...this.searchConfig,
      title: undefined,
      entities: this.presentEntities,
      entity: undefined,
      hideSelectedEntitiesList: true,
      insertEntityManually: (entity: T) => {
        const entityId = ko.unwrap(entity.id!);
        const alreadyPresent = this.group.rules().some(
          (rule) =>
            rule.type === 'rule' && ko.unwrap(rule.entity.id!) === entityId
        );
        if (alreadyPresent) {
          return;
        }
        this.group.rules.push({ type: 'rule', entity: entity });
        this.onChange();
      },
    };
  }

  dispose() {
    this.combinatorLabel.dispose();
    this.subFilterLabel.dispose();
    this.subSectionHeader.dispose();
    this.conditionCount.dispose();
    for (const sub of this.subscriptions) {
      sub.dispose();
    }
    this.subscriptions = [];
  }

  toggleCombinator = () => {
    const current = this.group.combinator();
    this.group.combinator(current === Combinator.And ? Combinator.Or : Combinator.And);
    this.onChange();
  };

  setCombinator = (value: Combinator) => {
    if (this.group.combinator() === value) {
      return;
    }
    this.group.combinator(value);
    this.onChange();
  };

  addGroup = () => {
    this.group.rules.push(createRuleGroup<T>(Combinator.Or));
    this.onChange();
  };

  addSubFilter = () => {
    if (!this.canAddSubFilter) {
      return;
    }
    const inverse =
      this.group.combinator() === Combinator.And ? Combinator.Or : Combinator.And;
    this.group.rules.push(createRuleGroup<T>(inverse));
    this.onChange();
  };

  removeItem = (item: Rule<T> | RuleGroup<T>) => {
    this.group.rules.remove(item);
    this.onChange();
  };

  static createViewModel<T extends IdData>(
    params: ConditionBuilderParams<T>,
    _componentInfo: KnockoutComponentTypes.ComponentInfo
  ) {
    return new ConditionBuilder(params);
  }
}

ko.components.register('condition-builder', {
  viewModel: { createViewModel: ConditionBuilder.createViewModel },
  template: template,
});
